<template>
	<div class="input-group" :class="{'has-error': hasErrors, horizontal}">
		<slot v-if="labelPosition == 'end'" />
		<label :for="id" class="label" :class="[{'sr-only': hideLabel}, labelClass]">
			<slot v-if="$slots.label" name="label" />
			<template v-else>
				{{ label }}
				<span v-if="isRequired" class="text-red">*</span>
			</template>
			<div
				v-if="horizontal && (hasErrors || $slots.error)"
				:id="'form-error-' + id"
				class="input-error"
				:class="[errorLabelClass]"
			>
				<slot name="error">{{ errorMessage }}</slot>
			</div>
		</label>
		<slot v-if="labelPosition == 'start'" />
		<div
			v-if="!horizontal && (hasErrors || $slots.error)"
			:id="'form-error-' + id"
			class="input-error"
			:class="[errorLabelClass]"
		>
			<slot name="error">{{ errorMessage }}</slot>
		</div>
	</div>
</template>

<script>
export default {
	name: 'BaseInputGroup',
	props: {
		id: {type: String, required: true},
		label: {type: String, required: true},
		hideLabel: Boolean,
		labelPosition: {
			default: () => 'start',
			type: String,
			validator: value => ['start', 'end'].includes(value),
		},
		horizontal: {type: Boolean},
		labelClass: {type: String},
		validation: {type: Object, default: () => ({})},
		errorLabelClass: {type: String},
		required: Boolean,
	},
	data() {
		return {
			input: null,
		}
	},
	computed: {
		hasErrors() {
			return Boolean(this.validation.$error)
		},
		errorMessage() {
			return this.validation?.$errors?.[0]?.$message
		},
		isRequired() {
			return 'required' in this.validation || this.required
		},
	},
	watch: {
		hasErrors() {
			this.updateInputAriaAttributes()
		},
		isRequired() {
			this.updateInputAriaAttributes()
		},
	},
	async mounted() {
		await this.$nextTick()
		this.input = this.$el.querySelector('input, select, textarea', '[contenteditable=true]')
		if (!this.input) return
		this.input.setAttribute('id', this.id)
	},
	methods: {
		async updateInputAriaAttributes() {
			if (!this.input) return
			this.input.setAttribute('aria-invalid', this.hasErrors)
			this.input.setAttribute('aria-describedby', this.hasErrors ? `form-error-` + this.id : '')
			this.input.setAttribute('aria-required', this.isRequired)
		},
	},
}
</script>

<style scoped>
.input-group {
	display: flex;
	gap: 6px;
	flex-direction: column;
	align-items: flex-start;
	align-content: flex-start;
}

.input-group.horizontal {
	flex-direction: row;
	flex-wrap: wrap;
}

.label {
	display: block;
	margin-left: 4px;
	color: var(--gray-300);
	flex-basis: min-content;
	flex-grow: 1;
}

.horizontal .label {
	margin-left: 0;
}

.input-error {
	color: var(--red-500);
	font-size: var(--font-size-sm);
	margin-left: 4px;
}

.horizontal .input-error {
	flex-basis: 100%;
	margin-top: 8px;
	margin-left: 0;
}

/* eslint-disable vue-scoped-css/no-unused-selector */
.has-error .input:focus,
.has-error .input:hover {
	border-color: var(--red-500);
}

.has-error .input,
.has-error input[type='checkbox'],
.has-error .input-select {
	border-color: var(--red-500);
}
</style>

<style>
form .input-group {
	margin-bottom: 16px;
}
</style>
